import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Loader2, X, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useVehicleMakes, useVehicleModels, VehicleMake, VehicleModel } from '@/hooks/useVehicleMakesModels';
import { useDebounce } from '@/hooks/useDebounce';

// ============================================================================
// Vehicle Make Autocomplete
// ============================================================================

interface VehicleMakeAutocompleteProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
}

export const VehicleMakeAutocomplete: React.FC<VehicleMakeAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Select make...',
	disabled = false,
	className = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

	const debouncedSearch = useDebounce(inputValue, 200);
	const { makes, loading, error } = useVehicleMakes(debouncedSearch);

	const commitTypedValue = useCallback(() => {
		if (inputValue !== value) {
			onChange(inputValue);
		}
	}, [inputValue, onChange, value]);

	// Sync input value with external value
	useEffect(() => {
		setInputValue(value);
	}, [value]);

	// Handle click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const clickedOutsideContainer = containerRef.current && !containerRef.current.contains(event.target as Node);
			const clickedOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target as Node);

			if (clickedOutsideContainer && clickedOutsideDropdown) {
				setIsOpen(false);
				commitTypedValue();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [commitTypedValue]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0) {
			const itemElement = itemRefs.current.get(highlightedIndex);
			itemElement?.scrollIntoView({ block: 'nearest' });
		}
	}, [highlightedIndex]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setIsOpen(true);
		setHighlightedIndex(-1);
	};

	const handleSelect = useCallback((make: VehicleMake) => {
		setInputValue(make.name);
		onChange(make.name);
		setIsOpen(false);
		setHighlightedIndex(-1);
	}, [onChange]);

	const handleClear = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setInputValue('');
		onChange('');
		inputRef.current?.focus();
	}, [onChange]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			if (e.key === 'ArrowDown' || e.key === 'Enter') {
				setIsOpen(true);
				e.preventDefault();
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setHighlightedIndex(prev =>
					prev < makes.length - 1 ? prev + 1 : prev
				);
				break;
			case 'ArrowUp':
				e.preventDefault();
				setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
				break;
			case 'Enter':
				e.preventDefault();
				if (highlightedIndex >= 0 && makes[highlightedIndex]) {
					handleSelect(makes[highlightedIndex]);
				} else {
					commitTypedValue();
					setIsOpen(false);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				setInputValue(value);
				break;
			case 'Tab':
				commitTypedValue();
				setIsOpen(false);
				break;
		}
	};

	const dropdownContent = (
		<DropdownPortal containerRef={containerRef} dropdownRef={dropdownRef} isOpen={isOpen}>
			<div
				ref={listRef}
				className="overflow-y-auto py-1"
			>
				{loading ? (
					<div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
						<Loader2 className="w-4 h-4 mr-2 animate-spin" />
						Loading makes...
					</div>
				) : error ? (
					<div className="flex items-center justify-center py-4 text-sm text-destructive">
						<AlertCircle className="w-4 h-4 mr-2" />
						Failed to load makes
					</div>
				) : makes.length === 0 ? (
					<div className="py-4 text-center text-sm text-muted-foreground">
						No makes found
					</div>
				) : (
					makes.map((make, index) => (
						<div
							key={make.id}
							ref={(el) => {
								if (el) itemRefs.current.set(index, el);
								else itemRefs.current.delete(index);
							}}
							className={cn(
								'flex items-center px-3 py-2 cursor-pointer text-sm',
								'hover:bg-accent hover:text-accent-foreground',
								highlightedIndex === index && 'bg-accent text-accent-foreground',
								value === make.name && 'font-medium'
							)}
							onClick={() => handleSelect(make)}
							onMouseEnter={() => setHighlightedIndex(index)}
						>
							<span className="flex-1">{make.name}</span>
							{value === make.name && (
								<Check className="w-4 h-4 text-primary" />
							)}
						</div>
					))
				)}
			</div>
		</DropdownPortal>
	);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => setIsOpen(true)}
					placeholder={placeholder}
					disabled={disabled}
					className={cn(
						'flex h-9 w-full rounded-md border border-input bg-[#fafafa] dark:bg-slate-900 px-3 py-1 text-sm font-semibold shadow-xs transition-colors',
						'placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal',
						'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
						'disabled:cursor-not-allowed disabled:opacity-50',
						'pr-16' // Space for icons
					)}
				/>
				<div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
					{inputValue && !disabled && (
						<button
							type="button"
							onClick={handleClear}
							className="p-1 hover:bg-accent rounded"
							aria-label="Clear selection"
						>
							<X className="w-3.5 h-3.5 text-muted-foreground" />
						</button>
					)}
					<button
						type="button"
						onClick={() => !disabled && setIsOpen(!isOpen)}
						className="p-1 hover:bg-accent rounded cursor-pointer disabled:cursor-not-allowed"
						disabled={disabled}
						aria-label={isOpen ? 'Close dropdown' : 'Open dropdown'}
					>
						<ChevronDown
							className={cn(
								'w-4 h-4 text-muted-foreground transition-transform',
								isOpen && 'rotate-180'
							)}
						/>
					</button>
				</div>
			</div>
			{dropdownContent}
		</div>
	);
};

// ============================================================================
// Vehicle Model Autocomplete
// ============================================================================

interface VehicleModelAutocompleteProps {
	value: string;
	onChange: (value: string) => void;
	makeName: string | null;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
}

export const VehicleModelAutocomplete: React.FC<VehicleModelAutocompleteProps> = ({
	value,
	onChange,
	makeName,
	placeholder = 'Select model...',
	disabled = false,
	className = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

	const debouncedSearch = useDebounce(inputValue, 200);
	const { models, loading, error } = useVehicleModels(makeName, debouncedSearch);

	const commitTypedValue = useCallback(() => {
		if (inputValue !== value) {
			onChange(inputValue);
		}
	}, [inputValue, onChange, value]);

	const isDisabled = disabled;
	const actualPlaceholder = placeholder;

	// Reset value when make changes
	useEffect(() => {
		if (!makeName) {
			setInputValue('');
		}
	}, [makeName]);

	// Sync input value with external value
	useEffect(() => {
		setInputValue(value);
	}, [value]);

	// Handle click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const clickedOutsideContainer = containerRef.current && !containerRef.current.contains(event.target as Node);
			const clickedOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target as Node);

			if (clickedOutsideContainer && clickedOutsideDropdown) {
				setIsOpen(false);
				commitTypedValue();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [commitTypedValue]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0) {
			const itemElement = itemRefs.current.get(highlightedIndex);
			itemElement?.scrollIntoView({ block: 'nearest' });
		}
	}, [highlightedIndex]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setIsOpen(true);
		setHighlightedIndex(-1);
	};

	const handleSelect = useCallback((model: VehicleModel) => {
		setInputValue(model.name);
		onChange(model.name);
		setIsOpen(false);
		setHighlightedIndex(-1);
	}, [onChange]);

	const handleClear = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setInputValue('');
		onChange('');
		inputRef.current?.focus();
	}, [onChange]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			if (e.key === 'ArrowDown' || e.key === 'Enter') {
				setIsOpen(true);
				e.preventDefault();
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setHighlightedIndex(prev =>
					prev < models.length - 1 ? prev + 1 : prev
				);
				break;
			case 'ArrowUp':
				e.preventDefault();
				setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
				break;
			case 'Enter':
				e.preventDefault();
				if (highlightedIndex >= 0 && models[highlightedIndex]) {
					handleSelect(models[highlightedIndex]);
				} else {
					commitTypedValue();
					setIsOpen(false);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				setInputValue(value);
				break;
			case 'Tab':
				commitTypedValue();
				setIsOpen(false);
				break;
		}
	};

	const dropdownContent = (
		<DropdownPortal containerRef={containerRef} dropdownRef={dropdownRef} isOpen={isOpen && !!makeName}>
			<div
				ref={listRef}
				className="overflow-y-auto py-1"
			>
				{loading ? (
					<div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
						<Loader2 className="w-4 h-4 mr-2 animate-spin" />
						Loading models...
					</div>
				) : error ? (
					<div className="flex items-center justify-center py-4 text-sm text-destructive">
						<AlertCircle className="w-4 h-4 mr-2" />
						Failed to load models
					</div>
				) : models.length === 0 ? (
					<div className="py-4 text-center text-sm text-muted-foreground">
						{inputValue ? 'No models found' : 'No models available for this make'}
					</div>
				) : (
					models.map((model, index) => (
						<div
							key={model.id}
							ref={(el) => {
								if (el) itemRefs.current.set(index, el);
								else itemRefs.current.delete(index);
							}}
							className={cn(
								'flex items-center px-3 py-2 cursor-pointer text-sm',
								'hover:bg-accent hover:text-accent-foreground',
								highlightedIndex === index && 'bg-accent text-accent-foreground',
								value === model.name && 'font-medium'
							)}
							onClick={() => handleSelect(model)}
							onMouseEnter={() => setHighlightedIndex(index)}
						>
							<span className="flex-1">{model.name}</span>
							{value === model.name && (
								<Check className="w-4 h-4 text-primary" />
							)}
						</div>
					))
				)}
			</div>
		</DropdownPortal>
	);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => setIsOpen(true)}
					placeholder={actualPlaceholder}
					disabled={isDisabled}
					className={cn(
						'flex h-9 w-full rounded-md border border-input bg-[#fafafa] dark:bg-slate-900 px-3 py-1 text-sm font-semibold shadow-xs transition-colors',
						'placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal',
						'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
						'disabled:cursor-not-allowed disabled:opacity-50',
						'pr-16' // Space for icons
					)}
				/>
				<div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
					{inputValue && !isDisabled && (
						<button
							type="button"
							onClick={handleClear}
							className="p-1 hover:bg-accent rounded"
							aria-label="Clear selection"
						>
							<X className="w-3.5 h-3.5 text-muted-foreground" />
						</button>
					)}
					{loading ? (
						<Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
					) : (
						<button
							type="button"
							onClick={() => !isDisabled && setIsOpen(!isOpen)}
							className="p-1 hover:bg-accent rounded cursor-pointer disabled:cursor-not-allowed"
							disabled={isDisabled}
							aria-label={isOpen ? 'Close dropdown' : 'Open dropdown'}
						>
							<ChevronDown
								className={cn(
									'w-4 h-4 text-muted-foreground transition-transform',
									isOpen && 'rotate-180'
								)}
							/>
						</button>
					)}
				</div>
			</div>
			{dropdownContent}
		</div>
	);
};

// ============================================================================
// Dropdown Portal Component with Smart Positioning
// ============================================================================

interface DropdownPortalProps {
	children: React.ReactNode;
	containerRef: React.RefObject<HTMLDivElement | null>;
	dropdownRef: React.RefObject<HTMLDivElement | null>;
	isOpen: boolean;
}

const DropdownPortal: React.FC<DropdownPortalProps> = ({ children, containerRef, dropdownRef, isOpen }) => {
	const [position, setPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUpwards: false
	});

	useEffect(() => {
		const updatePosition = () => {
			if (containerRef.current && isOpen) {
				const rect = containerRef.current.getBoundingClientRect();
				const dropdownMaxHeight = 240; // max-h-60 = 15rem = 240px
				const offset = 4; // offset for spacing
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				// Vertical collision detection
				const spaceBelow = viewportHeight - rect.bottom;
				const spaceAbove = rect.top;
				const shouldOpenUpwards = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

				// Horizontal collision detection
				let left = rect.left;
				const dropdownWidth = rect.width;

				// Check if dropdown would overflow on the right
				if (rect.left + dropdownWidth > viewportWidth) {
					left = viewportWidth - dropdownWidth - 8;
				}

				// Check if dropdown would overflow on the left
				if (left < 0) {
					left = 8;
				}

				setPosition({
					top: shouldOpenUpwards
						? rect.top - offset
						: rect.bottom + offset,
					left: left,
					width: rect.width,
					openUpwards: shouldOpenUpwards
				});
			}
		};

		if (isOpen) {
			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);

			return () => {
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [containerRef, isOpen]);

	if (!isOpen || position.width === 0) {
		return null;
	}

	return createPortal(
		<div
			ref={dropdownRef as React.RefObject<HTMLDivElement>}
			className={cn(
				'fixed z-50 bg-popover border border-border rounded-md shadow-md max-h-60 overflow-y-auto',
				'animate-in fade-in-0 zoom-in-95',
				position.openUpwards ? 'origin-bottom' : 'origin-top'
			)}
			style={{
				top: position.openUpwards ? 'auto' : `${position.top}px`,
				bottom: position.openUpwards ? `${window.innerHeight - position.top}px` : 'auto',
				left: `${position.left}px`,
				width: `${position.width}px`
			}}
			onMouseDown={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		document.body
	);
};
